import * as moment from 'moment';
import { ProviderCredential } from '../../admin/admin.models';
import { VehicleDTO } from '../vehicle/models/vehicle.models';

/**
 * Plan types.
 */
export enum PlanType {
  CONSUMER = 'consumer',
  COMMERCIAL = 'commercial',
}

/**
 * Subscription cadence.
 */
export enum SubscriptionCadence {
  DAILY = 'daily',
  MONTHLY = 'monthly',
  ANNUAL = 'annual',
  LIFETIME = 'lifetime',
}

export enum SubscriptionPlanTier {
  TRACKED = 'tracked',
  UNTRACKED = 'untracked',
}

/**
 * Subscription Plan as returned by the API.
 */
export type SubscriptionPlanDTO = {
  id: number;
  name: string;
  cadence: SubscriptionCadence;
  type: PlanType;
  price_per_spot: number;
  max_spots?: number;
  display_ads: boolean;
  mobile: boolean;
  free: boolean;
  currency: string;
  tier: SubscriptionPlanTier;
  // TODO: Standardize Provider(OEM) ID vs Name usage across platform
  for_provider_id?: number;
  for_provider_name?: string;
};

export type SubscriptionPlanShort = {
  id: number;
  name: string;
  cadence: SubscriptionCadence;
  for_provider: string;
  price_per_spot: number;
  max_spots: number;
  currency: string;
  tier: SubscriptionPlanTier;
};

export type SubscriptionPlanFilters = {
  mobile?: boolean;
  type?: PlanType;
  currency?: string;
  cadence?: SubscriptionCadence;
};

/**
 * Subscription states.
 */
export enum SubscriptionState {
  PENDING = 'pending', // Waiting for user to enter payment details
  TRIALING = 'trialing', // Trial period (for paid subscriptions)
  ACTIVE = 'active', // Active
  CANCELING = 'canceling', // Canceled but still active (for paid subscriptions)
  PAST_DUE = 'past_due', // Active but payment failed (for paid subscriptions)
  EXPIRED = 'expired', // Subscription ended (for paid subscriptions)
  TRIAL_END = 'trial_end', // Trial ended (For paid subscriptions)
}

/**
 * States with access to the platform
 */
export const ACTIVE_SUBSCRIPTION_STATES: SubscriptionState[] = [
  SubscriptionState.TRIALING,
  SubscriptionState.ACTIVE,
  SubscriptionState.CANCELING,
  SubscriptionState.PAST_DUE,
];

/**
 * States without access to the platform
 */
export const PENDING_SUBSCRIPTION_STATES = [
  SubscriptionState.PENDING,
  SubscriptionState.EXPIRED,
  SubscriptionState.TRIAL_END,
];

/**
 * Subscription Plan Instance as returned by the API.
 */
export type SubscriptionPlanInstanceDTO = {
  id: number;
  plan: SubscriptionPlanDTO;
};

/**
 * Subscription as returned by the API.
 */
export type SubscriptionDTO = {
  id: number | null;
  company: number;
  type: PlanType;
  cadence: SubscriptionCadence;
  mobile: boolean;
  preferred_currency: string;
  state: SubscriptionState;
  start_date: string;
  is_free: boolean;
  is_active: boolean;
  is_downgrading: boolean;
  display_ads: boolean;
  next_billing_date: string | null;
  days_until_next_bill: number | null;
  grace_period_days: number;
  items: {
    id?: number;
    purchased_spots: number;
    plan_instance_id: number;
    plan: SubscriptionPlanShort;
  }[];
};

/**
 * Display names for the subscription states.
 */
export const SUBSCRIPTION_STATE_DISPLAY: { [key in SubscriptionState]: string } = {
  [SubscriptionState.PENDING]: 'subscription_state_pending',
  [SubscriptionState.TRIALING]: 'subscription_state_trialing',
  [SubscriptionState.ACTIVE]: 'subscription_state_active',
  [SubscriptionState.CANCELING]: 'subscription_state_canceling',
  [SubscriptionState.PAST_DUE]: 'subscription_state_past_due',
  [SubscriptionState.EXPIRED]: 'subscription_state_expired',
  [SubscriptionState.TRIAL_END]: 'subscription_state_trial_end',
};

/**
 * Icon for the subscription states.
 */
export const SUBSCRIPTION_STATE_ICON: {
  [key in SubscriptionState]: 'checkmark' | 'block' | 'error_outline';
} = {
  [SubscriptionState.PENDING]: 'error_outline',
  [SubscriptionState.TRIALING]: 'checkmark',
  [SubscriptionState.ACTIVE]: 'checkmark',
  [SubscriptionState.CANCELING]: 'error_outline',
  [SubscriptionState.PAST_DUE]: 'block',
  [SubscriptionState.EXPIRED]: 'block', // change if needed
  [SubscriptionState.TRIAL_END]: 'block', // change if needed
};

/**
 * Cadence period.
 */
export const CADENCE_PERIOD: { [key in SubscriptionCadence]: string } = {
  [SubscriptionCadence.DAILY]: 'day',
  [SubscriptionCadence.MONTHLY]: 'month',
  [SubscriptionCadence.ANNUAL]: 'year',
  [SubscriptionCadence.LIFETIME]: 'lifetime',
};

/**
 * Cadence display.
 */
export const CADENCE_DISPLAY: { [key in SubscriptionCadence]: string } = {
  [SubscriptionCadence.DAILY]: 'Daily',
  [SubscriptionCadence.MONTHLY]: 'Monthly',
  [SubscriptionCadence.ANNUAL]: 'Yearly',
  [SubscriptionCadence.LIFETIME]: 'Lifetime',
};

/**
 * Days in cadence period.
 */
export const CADENCE_DAYS: { [key in SubscriptionCadence]: number } = {
  [SubscriptionCadence.DAILY]: 1,
  [SubscriptionCadence.MONTHLY]: moment().daysInMonth(),
  [SubscriptionCadence.ANNUAL]: moment().isLeapYear() ? 366 : 365,
  [SubscriptionCadence.LIFETIME]: 0,
};

export type PostSubscriptionVehicleData = {
  vehicles: VehicleDTO[];
  untrackedVehicles: VehicleDTO[];
  connectionProvider: ProviderCredential | undefined;
  fleets?: number[];
};

type SubscriptionClassOptField =
  | 'id'
  | 'items'
  | 'state'
  | 'grace_period_days'
  | 'preferred_currency'
  | 'next_billing_date'
  | 'days_until_next_bill'
  | 'is_free'
  | 'display_ads';

type SubscriptionDTOInput = Omit<SubscriptionDTO, SubscriptionClassOptField | 'is_active'> &
  Partial<Pick<SubscriptionDTO, SubscriptionClassOptField>>;

export class Subscription implements SubscriptionDTO {
  id = this._options.id ?? null;
  company = this._options.company;
  type = this._options.type;
  cadence = this._options.cadence;
  mobile = this._options.mobile;
  preferred_currency = this._options.preferred_currency ?? 'USD';

  set state(state: SubscriptionState) {
    this._state = state;
    this.is_active = [
      SubscriptionState.ACTIVE,
      SubscriptionState.CANCELING,
      SubscriptionState.PAST_DUE,
      SubscriptionState.PENDING,
      SubscriptionState.TRIALING,
      SubscriptionState.TRIAL_END,
    ].includes(state);
  }
  get state() {
    return this._state;
  }
  private _state = this._options.state ?? SubscriptionState.PENDING;
  is_active = false;

  start_date = this._options.start_date;
  grace_period_days = this._options.grace_period_days ?? 7;
  next_billing_date = this._options.next_billing_date ?? this._getNextBillingDate();
  days_until_next_bill = this._options.days_until_next_bill ?? this._getDaysUntilNextBill();

  /**
   * List of line items under a subscription
   * where each line is a product plan with purchased quantity
   */
  items = this._options.items ?? [];

  /** Whether the type of the subscription is Commercial (Fleet) */
  readonly isCommercial = this.type === PlanType.COMMERCIAL;

  /** Whether the total price for all vehicles plans under the subscription is 0 */
  readonly is_free = this._options.is_free ?? true;
  readonly isFree = this._options.is_free ?? true;

  readonly isTrialing = this.state === SubscriptionState.TRIALING;

  readonly isCancelling = this.state === SubscriptionState.CANCELING;
  readonly is_downgrading = this._options.is_downgrading;

  readonly display_ads = this._options.display_ads ?? false;
  readonly displayAds = this._options.display_ads ?? false;

  /** Calculated total monetary amount for all items */
  get total() {
    return this._total;
  }
  private _total = this.items.reduce(
    (acc, i) => acc + i.purchased_spots * i.plan.price_per_spot,
    0
  );

  constructor(private readonly _options: SubscriptionDTOInput) {}

  /** Generates a SubscriptionDTO object from this instance for the provided company id */
  dto(company: number): SubscriptionDTO {
    return {
      id: this.id,
      company,
      type: this.type,
      cadence: this.cadence,
      mobile: this.mobile,
      start_date: this.start_date,
      preferred_currency: this.preferred_currency,
      state: this.state,
      is_free: this.is_free,
      is_downgrading: this.is_downgrading,
      is_active: this.is_active,
      display_ads: this.display_ads,
      grace_period_days: this.grace_period_days,
      next_billing_date: this.next_billing_date,
      days_until_next_bill: this.days_until_next_bill,
      items: this.items,
    };
  }

  private _getNextBillingDate(): string | null {
    switch (this.cadence) {
      case SubscriptionCadence.ANNUAL:
        return (
          this.type === PlanType.COMMERCIAL
            ? moment(this.start_date).utc().add(1, 'y').startOf('y')
            : moment(this.start_date).utc().add(1, 'y')
        ).format();
      case SubscriptionCadence.MONTHLY:
        return (
          this.type === PlanType.COMMERCIAL
            ? moment(this.start_date).utc().add(1, 'M').startOf('M')
            : moment(this.start_date).utc().add(1, 'M')
        ).format();
      case SubscriptionCadence.DAILY:
        return moment(this.start_date).utc().add(1, 'd').format();
      case SubscriptionCadence.LIFETIME:
        return null;
    }
  }

  private _getDaysUntilNextBill(): number | null {
    return this.next_billing_date ? moment(this.next_billing_date).diff(moment(), 'days') : null;
  }
}
